// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the "which list?" search for New task — every list in every space,
// recent ones first, matched on list, folder and space name.

import type {
	WorkspaceFolderNode,
	WorkspaceListNode,
	WorkspaceSpaceNode,
} from '@app/features/konvo_tasks/state/KonvoWorkspace';

export interface ListSearchEntry {
	list: WorkspaceListNode;
	space: WorkspaceSpaceNode;
	folder: WorkspaceFolderNode | null;
	/** "Customer Services / Ticketing" */
	path: string;
	recent: boolean;
}

export function searchLists(
	all: Array<{list: WorkspaceListNode; space: WorkspaceSpaceNode; folder: WorkspaceFolderNode | null}>,
	recentIds: Array<string>,
	query: string,
	limit = 40,
): {recent: Array<ListSearchEntry>; matches: Array<ListSearchEntry>} {
	const q = query.trim().toLowerCase();
	const entries = all.map(({list, space, folder}) => ({
		list,
		space,
		folder,
		path: folder ? `${space.name} / ${folder.name}` : space.name,
		recent: recentIds.includes(list.id),
	}));
	const score = (entry: ListSearchEntry): number => {
		if (!q) return 1;
		const name = entry.list.name.toLowerCase();
		if (name.startsWith(q)) return 3;
		if (name.includes(q)) return 2;
		return entry.path.toLowerCase().includes(q) ? 1 : 0;
	};
	const scored = entries.filter((entry) => score(entry) > 0);
	const recent = recentIds
		.map((id) => scored.find((entry) => entry.list.id === id))
		.filter((entry): entry is ListSearchEntry => entry != null);
	const matches = scored
		.filter((entry) => !entry.recent)
		.sort((a, b) => score(b) - score(a))
		.slice(0, limit);
	return {recent, matches};
}

/** Splits a name around the first match so the UI can bold it. */
export function splitMatch(text: string, query: string): [string, string, string] {
	const q = query.trim().toLowerCase();
	const index = q ? text.toLowerCase().indexOf(q) : -1;
	if (index < 0) return [text, '', ''];
	return [text.slice(0, index), text.slice(index, index + q.length), text.slice(index + q.length)];
}
